<script setup lang="ts">
import { computed } from 'vue';
import FaCheckSquare from '@/components/icons/FaCheckSquare.vue';
import FaRegSquare from '@/components/icons/FaRegSquare.vue';
import FormatTime from '@/components/FormatTime.vue';
import type { Transaction, FullTransaction } from './types';
import { TransactionType } from '@/common/config';
import { isPaymentTransaction, isBetTransactionType } from '@/common/transaction-utils';
import { TransactionDisplayData, useTransactionDisplay } from './composables/useTransactionDisplay';
import {
  useTransactionCardCustomisation,
  useUserSelectedTransactionCardCustomisation,
} from './composables/useTransactionItemCustomisation';
import * as SUtils from '@/common/customisation/utils/styleUtils';

export type TransactionItemSide = 'agent' | 'user';

export interface TransactionItemProps {
  transaction: FullTransaction;
  transactionType: TransactionType;
  displayData?: TransactionDisplayData;
  showCheckbox?: boolean;
  editable?: boolean;
  isSelected?: boolean;
  disabled?: boolean;
  side?: TransactionItemSide;
}

const props = withDefaults(defineProps<TransactionItemProps>(), {
  showCheckbox: true,
  editable: false,
  isSelected: false,
  disabled: false,
  side: 'agent',
});

const { getTransactionDisplay, getBetTransactionDisplay } = useTransactionDisplay();

const emit = defineEmits<{
  (e: 'toggle', transaction: FullTransaction): void;
}>();

// Agent side customisation
const agentCustomisation = useTransactionCardCustomisation();

// User side customisation
const userCustomisation = useUserSelectedTransactionCardCustomisation();

// Side check computed
const isUserSide = computed(() => props.side === 'user');

// Computed styles based on side
const containerStyle = computed(() => {
  if (isUserSide.value) {
    return SUtils.combineStyles(
      SUtils.createBackgroundStyle(userCustomisation.backgroundColor),
      SUtils.createBorderStyle(userCustomisation.borderColor)
    );
  }

  return SUtils.combineStyles(
    SUtils.createBackgroundStyle(
      props.isSelected ? agentCustomisation.selectedBackgroundColor : agentCustomisation.backgroundColor
    ),
    SUtils.createBorderStyle(props.isSelected ? agentCustomisation.selectedBorderColor : agentCustomisation.borderColor)
  );
});

const checkboxStyle = computed(() => {
  if (isUserSide.value) {
    return SUtils.createTextStyle(userCustomisation.actionIconColor);
  }
  return SUtils.createTextStyle(props.isSelected ? agentCustomisation.selectedIconColor : agentCustomisation.iconColor);
});

const titleStyle = computed(() => {
  const textColor = isUserSide.value ? userCustomisation.textColor : agentCustomisation.textColor;
  return SUtils.createTextStyle(textColor);
});

const detailsStyle = computed(() => {
  const subTextColor = isUserSide.value ? userCustomisation.subTextColor : agentCustomisation.subTextColor;
  return SUtils.createTextStyle(subTextColor);
});

const separatorStyle = computed(() => {
  const borderColor = isUserSide.value ? userCustomisation.borderColor : agentCustomisation.borderColor;
  return SUtils.createBackgroundStyle(borderColor);
});

// Extract display data from the transaction object
const displayData = computed(() => {
  // For nested transactions, use the provided display data
  if (props.displayData) {
    return props.displayData;
  }

  const tx = props.transaction;

  // Check if it's a full transaction (TransStatement or BetHistoryEntity)
  if (isPaymentTransaction(tx)) {
    return getTransactionDisplay(tx, props.transactionType);
  }

  if (isBetTransactionType(tx)) {
    return getBetTransactionDisplay(tx);
  }

  // It's already a simple Transaction object
  return tx as Transaction;
});

const handleToggle = () => {
  if (props.disabled || !props.editable) return;
  emit('toggle', props.transaction);
};
</script>

<template>
  <div
    class="card-radius transaction-item"
    :class="{ selected: isSelected, disabled: disabled, 'non-selectable': !editable }"
    data-testid="transaction-item"
    :data-selected="isSelected ? 'true' : 'false'"
    :style="containerStyle"
    @click="handleToggle"
  >
    <div class="transaction-item__header">
      <span v-if="showCheckbox" class="transaction-item__checkbox-wrapper">
        <FaCheckSquare
          v-if="isSelected"
          height="2.1rem"
          width="2.1rem"
          class="checked transaction-item__checkbox"
          :style="checkboxStyle"
        />
        <FaRegSquare
          v-else
          height="2.1rem"
          width="2.1rem"
          class="transaction-item__checkbox unchecked"
          :style="checkboxStyle"
        />
      </span>

      <div class="separate" :style="separatorStyle" />

      <div class="transaction-item__content">
        <div class="theme-font-main transaction-item__title" :style="titleStyle">
          {{ displayData.title }}
        </div>
        <div class="transaction-item__details">
          <span class="theme-font-subtext transaction-item__date" :style="detailsStyle">
            <FormatTime :date="displayData.date" format="dd/MM/yyyy HH:mm:ss" parse />
          </span>
          <span class="theme-font-subtext transaction-item__id" :style="detailsStyle">TX ID: {{ displayData.id }}</span>
        </div>
      </div>

      <div class="transaction-item__right">
        <div class="theme-font-main transaction-item__amount" :style="titleStyle">
          {{ displayData.amount }}
        </div>
        <div class="theme-font-subtext transaction-item__status" :style="detailsStyle">
          {{ displayData.status }}
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.transaction-item {
  border: 1px solid;
  padding: 12px;
  margin-bottom: 10px;
  transition: all 0.2s ease;

  &:hover:not(.disabled):not(.non-selectable) {
    cursor: pointer;
  }

  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.non-selectable {
    cursor: default;
  }
}

.transaction-item__header {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.transaction-item__checkbox-wrapper {
  flex-shrink: 0;
  pointer-events: none;
}

.transaction-item__checkbox {
  flex-shrink: 0;
}

.separate {
  width: 1px;
  height: 20px;
  flex-shrink: 0;
}

.transaction-item__content {
  flex: 1;
  min-width: 0;
}

.transaction-item__title {
  margin-bottom: 6px;
}

.transaction-item__details {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.transaction-item__date,
.transaction-item__id {
  display: block;
}

.transaction-item__right {
  text-align: right;
  flex-shrink: 0;
}

.transaction-item__amount {
  margin-bottom: 4px;
}

.transaction-item__status {
  text-transform: capitalize;
}
</style>
